/**
 * useDialogOpenState — Phase 3.3 dialog hook factory primitive.
 *
 * The simplest piece of the dialog-hook pattern: a boolean `isOpen`
 * flag with stable `open()` / `close()` callbacks via `useCallback`.
 *
 * Each per-dialog hook (e.g. `useFontManagerDialog`, `useScaleListEditDialog`)
 * historically inlined this same 7-line boilerplate:
 *
 *   const [isOpen, setIsOpen] = useState(false);
 *   const open = useCallback(() => setIsOpen(true), []);
 *   const close = useCallback(() => setIsOpen(false), []);
 *
 * Lifting it to a shared primitive removes the duplicate while keeping
 * the per-hook contract identical (same identity stability, same
 * referential semantics for `open` / `close`).
 *
 * AutoCAD parity:
 *   Pure UI orchestration primitive — no DXF / AutoCAD semantics.
 */

import { useCallback, useRef, useState } from "react";

export interface UseDialogOpenStateOptions {
  initialOpen?: boolean;
  /**
   * Optional side effect to run *after* `setIsOpen(false)` in `close()`.
   * Use for compound close handlers that also reset adjacent state
   * (e.g. `setEditingStyleName(null)` in style-editor dialog hooks).
   *
   * The callback is captured into a ref so the returned `close`
   * identity remains stable across renders even if the caller passes
   * an inline arrow function.
   */
  onClose?: () => void;
}

export interface UseDialogOpenStateResult {
  isOpen: boolean;
  open: () => void;
  close: () => void;
}

export function useDialogOpenState(
  options: UseDialogOpenStateOptions | boolean = {},
): UseDialogOpenStateResult {
  // Legacy positional form: `useDialogOpenState(true)` → `{ initialOpen: true }`.
  const opts: UseDialogOpenStateOptions =
    typeof options === "boolean" ? { initialOpen: options } : options;
  const [isOpen, setIsOpen] = useState(opts.initialOpen ?? false);

  // Ref keeps the latest onClose without invalidating `close` identity.
  const onCloseRef = useRef(opts.onClose);
  onCloseRef.current = opts.onClose;

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => {
    setIsOpen(false);
    onCloseRef.current?.();
  }, []);
  return { isOpen, open, close };
}
